/**
 * Hook that runs a single-player Call Bridge game entirely in the browser.
 * Uses @callbridge/engine directly — no server connection needed.
 */

import { useCallback, useRef } from 'react';
import {
  type GameState as EngineGameState,
  type AIStrategy,
  type Card,
  type Suit,
  GamePhase,
  createGame,
  addPlayer,
  startGame,
  processCall,
  processPlayCard,
  determineTrickWinner,
  createEasyAI,
  createMediumAI,
  createHardAI,
} from '@callbridge/engine';
import { useGameStore } from '../stores';

type AIDifficulty = 'easy' | 'medium' | 'hard';

const AI_NAMES = ['Bot Alice', 'Bot Bob', 'Bot Charlie'];

function createAI(difficulty: AIDifficulty): AIStrategy {
  switch (difficulty) {
    case 'easy':
      return createEasyAI();
    case 'medium':
      return createMediumAI();
    case 'hard':
      return createHardAI();
  }
}

/**
 * Delay helper for animations between AI actions.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useSinglePlayerGame() {
  const store = useGameStore();
  const engineRef = useRef<EngineGameState | null>(null);
  const aisRef = useRef<Map<string, AIStrategy>>(new Map());
  const runningRef = useRef(false);
  const collectingRef = useRef(false);

  const PLAYER_ID = 'human';
  const AI_IDS = ['ai_0', 'ai_1', 'ai_2'];
  const AI_DELAY_MS = 800;
  const TRICK_COLLECT_MS = 1400;

  /**
   * Initialize and start a single-player game.
   */
  const startSinglePlayer = useCallback(
    (difficulty: AIDifficulty) => {
      // Create engine game
      let engine = createGame('sp_local');
      engine = addPlayer(engine, PLAYER_ID, 'You');

      const ais = new Map<string, AIStrategy>();
      for (let i = 0; i < 3; i++) {
        const aiId = AI_IDS[i]!;
        engine = addPlayer(engine, aiId, AI_NAMES[i]!);
        ais.set(aiId, createAI(difficulty));
      }

      engine = startGame(engine);
      engineRef.current = engine;
      aisRef.current = ais;

      // Sync to Zustand store
      syncEngineToStore(engine);
      runningRef.current = true;

      // If AI goes first, trigger AI turns
      void runAITurnsIfNeeded();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Human player makes a call.
   */
  const makeCall = useCallback(
    (call: number) => {
      if (!engineRef.current) return;

      engineRef.current = processCall(engineRef.current, PLAYER_ID, call);
      syncEngineToStore(engineRef.current);

      void runAITurnsIfNeeded();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Human player plays a card.
   */
  const playCard = useCallback(
    (suit: string, rank: number) => {
      if (!engineRef.current || collectingRef.current) return;

      applyPlayedCard(PLAYER_ID, { suit, rank });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Play a card for the given player, then sync to the store. If the card
   * completes a trick, briefly show the full trick (with the winner
   * highlighted) before clearing it, so the last card played is visible.
   */
  function applyPlayedCard(playerId: string, card: { suit: string; rank: number }) {
    if (!engineRef.current) return;

    const prevRound = engineRef.current.currentRound;
    const prevTrickCards = prevRound?.currentTrick.cards ?? [];
    const playerCount = prevRound?.players.length ?? 4;
    const trickCompleted = prevTrickCards.length === playerCount - 1;

    engineRef.current = processPlayCard(engineRef.current, playerId, card);

    if (trickCompleted) {
      const completedCards = [...prevTrickCards, { playerId, card: card as Card }];
      const leadSuit = prevRound?.currentTrick.leadSuit ?? completedCards[0]!.card.suit;
      const winnerId = determineTrickWinner(completedCards, leadSuit);

      syncEngineToStore(engineRef.current, {
        cards: completedCards,
        leadSuit,
        winnerId,
      });

      collectingRef.current = true;
      setTimeout(() => {
        collectingRef.current = false;
        useGameStore.getState().trickCleared();
        void runAITurnsIfNeeded();
      }, TRICK_COLLECT_MS);
      return;
    }

    syncEngineToStore(engineRef.current);
    void runAITurnsIfNeeded();
  }

  /**
   * Run AI turns sequentially with delays for animation.
   */
  async function runAITurnsIfNeeded() {
    if (!engineRef.current || !runningRef.current || collectingRef.current) return;

    const engine = engineRef.current;
    const round = engine.currentRound;
    if (!round) return;

    // Determine whose turn it is
    const currentPlayerId = round.players[round.currentPlayerIndex]?.id;
    if (!currentPlayerId || currentPlayerId === PLAYER_ID) return;

    const ai = aisRef.current.get(currentPlayerId);
    if (!ai) return;

    await delay(AI_DELAY_MS);

    // Re-read fresh state after delay — closured round/player are stale
    if (!engineRef.current || !runningRef.current || collectingRef.current) return;

    const freshEngine = engineRef.current;
    const freshRound = freshEngine.currentRound;
    if (!freshRound) return;

    // Guard: ensure it's still the same AI's turn
    const freshCurrentId = freshRound.players[freshRound.currentPlayerIndex]?.id;
    if (freshCurrentId !== currentPlayerId) return;

    const freshPlayer = freshRound.players[freshRound.currentPlayerIndex]!;

    try {
      if (freshEngine.phase === GamePhase.Calling) {
        const call = ai.makeCall(freshPlayer, freshRound);
        engineRef.current = processCall(engineRef.current, currentPlayerId, call);
        syncEngineToStore(engineRef.current);

        // Continue if next turn is also AI
        void runAITurnsIfNeeded();
      } else if (freshEngine.phase === GamePhase.Playing) {
        const card = ai.selectCard(freshPlayer, freshRound);
        applyPlayedCard(currentPlayerId, card);
      }
    } catch (err) {
      store.errorSet(err instanceof Error ? err.message : 'AI error');
    }
  }

  /**
   * Sync engine state to Zustand game store.
   *
   * `trickOverride` lets us display the just-completed trick (including the
   * final card played and the winner) for a moment before it's cleared,
   * since the engine resets `currentTrick` immediately once it's won.
   */
  function syncEngineToStore(
    engine: EngineGameState,
    trickOverride?: {
      cards: { playerId: string; card: Card }[];
      leadSuit: Suit | null;
      winnerId: string | null;
    },
  ) {
    const round = engine.currentRound;
    const humanIndex = engine.players.findIndex((p) => p.id === PLAYER_ID);

    // Map engine players to store format
    const players = engine.players.map((p, i) => ({
      id: p.id,
      name: p.name,
      seatIndex: i,
      isAI: p.id !== PLAYER_ID,
      isConnected: true,
      call: round?.players[i]?.call ?? null,
      tricksWon: round?.players[i]?.tricksWon ?? 0,
      totalScore: p.score,
      handSize: round?.players[i]?.hand.length,
    }));

    // Get human's hand
    const hand = round?.players[humanIndex]?.hand ?? [];

    // Map phase
    let phase: 'idle' | 'calling' | 'playing' | 'roundEnd' | 'gameOver' = 'idle';
    if (engine.phase === GamePhase.Calling) phase = 'calling';
    else if (engine.phase === GamePhase.Playing) phase = 'playing';
    else if (engine.phase === GamePhase.RoundEnd) phase = 'roundEnd';
    else if (engine.phase === GamePhase.GameOver) phase = 'gameOver';

    // Trick cards
    const trickCards = trickOverride
      ? trickOverride.cards
      : (round?.currentTrick.cards ?? []).map((entry) => ({
          playerId: entry.playerId,
          card: entry.card,
        }));
    const leadSuit = trickOverride
      ? trickOverride.leadSuit
      : (round?.currentTrick.leadSuit ?? null);
    const trickWinnerId = trickOverride ? trickOverride.winnerId : null;

    // Update store in one batch
    store.gameStarted(engine.id, players, humanIndex);

    // Override with more accurate state (gameStarted resets too much)
    useGameStore.setState({
      phase,
      hand,
      trickCards,
      leadSuit,
      trickWinnerId,
      showingCollection: !!trickOverride,
      currentTurnIndex: round?.currentPlayerIndex ?? 0,
      roundNumber: engine.roundNumber,
      winnerId: engine.winnerId,
      finalScores: engine.winnerId
        ? Object.fromEntries(engine.players.map((p) => [p.id, p.score]))
        : null,
    });

    // Handle round results
    if (engine.roundResults.length > 0) {
      const lastResult = engine.roundResults[engine.roundResults.length - 1]!;
      const roundResults = lastResult.playerResults.map((r) => ({
        playerId: r.playerId,
        call: r.call,
        tricksWon: r.tricksWon,
        roundScore: r.roundScore,
        totalScore: engine.players.find((p) => p.id === r.playerId)?.score ?? 0,
      }));

      useGameStore.setState({
        roundHistory: engine.roundResults.map((rr) =>
          rr.playerResults.map((r) => ({
            playerId: r.playerId,
            call: r.call,
            tricksWon: r.tricksWon,
            roundScore: r.roundScore,
            totalScore: engine.players.find((p) => p.id === r.playerId)?.score ?? 0,
          })),
        ),
        roundResults: engine.phase === GamePhase.RoundEnd ? roundResults : null,
      });
    }
  }

  /**
   * Reset the game.
   */
  const resetGame = useCallback(() => {
    runningRef.current = false;
    engineRef.current = null;
    aisRef.current.clear();
    store.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    startSinglePlayer,
    makeCall,
    playCard,
    resetGame,
    isRunning: runningRef.current,
  };
}
